import React, { useState, useEffect } from "react";
import "./CharacterDetailTemplate.css";
import { useGlobalState } from "../../context/GlobalStateContext";
import { updateCharacter } from "../../services/characters";
import CharacterDetailTemplateDesktop from "./CharacterDetailTemplate_Desktop";
import CharacterDetailTemplateMobile from "./CharacterDetailTemplate_Mobile";

function CharacterDetailTemplate({ character }) {
  const { socket, sendSocketMessage, isMobile, reloadCharacters } =
    useGlobalState();
  const [characterData, setCharacterData] = useState(character);
  const [editMode, setEditMode] = useState(false);

  // Sincronizza characterData quando character prop cambia
  useEffect(() => {
    setCharacterData(character);
  }, [character]);

  if (!character) {
    return <div>Loading...</div>;
  }

  const returnLevel = (dice, value) => {
    if (dice === 1) return "critic";
    if (dice <= 0.2 * value) return "extreme";
    if (dice <= 0.5 * value) return "arduous";
    if (dice <= value) return "normal";
    if (dice >= 99) return "extrafailed";
    return "failed";
  };

  const handleDiceRoll = (name, value) => {
    const d10 = () => {
      return Math.floor(Math.random() * 10) + 1;
    };
    const dice = (d10() - 1) * 10 + d10();
    const level = returnLevel(dice, value);
    const message = `<b>${character.characterInfo.name}</b> ha tirato su <b>${name}</b> ottenendo <b>${dice}</b> su <b>${value}</b>`;
    const nickname =
      character.characterInfo.nickname || character.characterInfo.name;
    let speachText = null;
    switch (level) {
      case "critic":
        speachText = `<b>${nickname}</b> ha ottenuto un critico su <b>${name}</b>`;
        break;
      case "extreme":
        speachText = `<b>${nickname}</b> ha ottenuto un tiro estremo su <b>${name}</b>`;
        break;
      case "arduous":
        speachText = `<b>${nickname}</b> ha ottenuto un tiro arduo su <b>${name}</b>`;
        break;
      case "normal":
        speachText = `<b>${nickname}</b> ha ottenuto un successo normale su <b>${name}</b>`;
        break;
      case "failed":
        speachText = `<b>${nickname}</b> ha tirato su <b>${name}</b> fallendo`;
        break;
      case "extrafailed":
        speachText = `<b>${nickname}</b> ha ottenuto un fallimento critico su <b>${name}</b>`;
        break;
      default:
        break;
    }

    if (socket) {
      sendSocketMessage("DICE_ROLL", { message, level, speachText });
    }
  };

  const handleDiceWeaponRoll = (
    weapon,
    selectedRange = null,
    selectedDamage = null,
    currentCharacter = null,
  ) => {
    // Usa currentCharacter se fornito, altrimenti fallback a character dalla closure
    const characterToUse = currentCharacter || character;

    // Se l'arma ha range multipli e non è stata selezionata una distanza, ritorna null per mostrare il popup
    if (!selectedRange && weapon.range && weapon.range.includes("/")) {
      return null;
    }

    // Usa il range e damage selezionati se disponibili, altrimenti usa quelli dell'arma
    const weaponToUse = selectedRange
      ? { ...weapon, range: selectedRange, damage: selectedDamage }
      : weapon;

    const BD = characterToUse.points.find(
      (point) => point.name === "Bonus al Danno",
    );
    const evaluateDamage = (damage) => {
      // Sostituisci BD con il valore effettivo se presente nella formula
      return damage.toLowerCase().replace(/\+?bd/gi, (match) => {
        return `${BD.value < 0 ? BD.value : `+${BD.value}`}`;
      });
    };
    const rollDice = (faces) => {
      return Math.floor(Math.random() * faces) + 1;
    };
    const evaluateExpression = (expression) => {
      const components = evaluateDamage(expression)
        .toLowerCase()
        .split(/(\+|-)/)
        .map((str) => str.trim());

      let result = 0;
      let maxResult = 0;
      let lastOperation = "+";
      let formulaParts = [];

      components.forEach((component) => {
        if (component.includes("d")) {
          const [numberOfDice, faces] = component
            .split("d")
            .map((str) => parseInt(str));

          let total = 0;
          let maxTotal =
            numberOfDice * faces * (lastOperation === "+" ? 1 : -1);
          for (let i = 0; i < numberOfDice; i++) {
            total += rollDice(faces);
          }

          // Aggiungi alla formula con il risultato
          const sign = lastOperation === "+" ? "+" : "-";
          formulaParts.push(`${sign}${Math.abs(total)}`);

          if (lastOperation === "-") {
            total *= -1;
            maxTotal *= -1;
          }
          result += total;
          maxResult += maxTotal;
        } else {
          if (component !== "+" && component !== "-") {
            let modifier = parseInt(component);
            if (lastOperation === "-") {
              modifier *= -1;
            }
            const sign = lastOperation === "+" ? "+" : "-";
            formulaParts.push(`${sign}${Math.abs(modifier)}`);
            result += modifier;
            maxResult += modifier;
          } else {
            lastOperation = component;
          }
        }
      });

      // Costruisci la formula finale, rimuovendo il primo + se presente
      let formula = formulaParts.join("").replace(/^\+/, "");

      return {
        result: result < 0 ? 0 : result,
        maxResult: maxResult < 0 ? 0 : maxResult,
        formula: formula,
      };
    };

    // Usa il valore della skill associata se presente, altrimenti usa weaponToUse.value
    let skillValue = 0;
    if (weaponToUse.associatedSkill) {
      const skill = characterToUse.skills.find(
        (s) => s.name === weaponToUse.associatedSkill,
      );
      skillValue = skill ? parseInt(skill.value) : 0;
    } else {
      skillValue = parseInt(weaponToUse.value) || 0;
    }

    const dice = (rollDice(10) - 1) * 10 + rollDice(10);
    const level = returnLevel(dice, skillValue);

    // Controlla se il danno è parsabile (contiene numeri e 'd')
    const damageString = weaponToUse.damage.replace(/ /g, "").toLowerCase();
    const isDamageParsable = /\d+d\d+|\d+/.test(damageString);

    if (dice > skillValue) {
      const message = `<b>${characterToUse.characterInfo.name}</b> ha usato <b>${weaponToUse.name}</b>: Ha ottenuto <b>${dice}/${skillValue}</b>`;
      const nickname =
        characterToUse.characterInfo.nickname ||
        characterToUse.characterInfo.name;
      let speachText = null;
      switch (level) {
        case "failed":
          speachText = `<b>${nickname}</b> non è riuscito ad usare <b>${weaponToUse.name}</b>`;
          break;
        case "extrafailed":
          speachText = `<b>${nickname}</b> non è riuscito ad usare <b>${weaponToUse.name}</b> facendo una figura di merda`;
          break;
        default:
          break;
      }
      if (socket) {
        sendSocketMessage("DICE_ROLL", { message, level, speachText });
      }
      return;
    }

    // Se il danno non è parsabile (es. "strangolamento", "presa", ecc.), tira solo l'abilità
    if (!isDamageParsable) {
      const rangeText = selectedRange ? ` a ${selectedRange}m` : "";
      const message = `<b>${characterToUse.characterInfo.name}</b> ha usato <b>${weaponToUse.name}</b>${rangeText}: Ha ottenuto <b>${dice}/${skillValue}</b>`;
      const nickname =
        characterToUse.characterInfo.nickname ||
        characterToUse.characterInfo.name;
      let speachText = null;
      switch (level) {
        case "critic":
          speachText = `<b>${nickname}</b> ha fatto un tiro critico con <b>${weaponToUse.name}</b>${rangeText}`;
          break;
        case "extreme":
          speachText = `<b>${nickname}</b> ha fatto un tiro estremo con <b>${weaponToUse.name}</b>${rangeText}`;
          break;
        case "arduous":
          speachText = `<b>${nickname}</b> ha fatto un tiro arduo con <b>${weaponToUse.name}</b>${rangeText}`;
          break;
        case "normal":
          speachText = `<b>${nickname}</b> ha usato <b>${weaponToUse.name}</b>${rangeText} con successo`;
          break;
        default:
          break;
      }
      if (socket) {
        sendSocketMessage("DICE_ROLL", { message, level, speachText });
      }
      return;
    }

    const damage = evaluateExpression(damageString);
    let evalDamage = damage.result;
    let damageFormula = `[${damage.formula}]`;

    if (level === "extreme" || level === "critic") {
      evalDamage += damage.maxResult;
      damageFormula = `[${damage.maxResult}+${damage.formula}]`;
    }

    const nickname =
      characterToUse.characterInfo.nickname ||
      characterToUse.characterInfo.name;

    // Se c'è una distanza selezionata, includila nel messaggio
    const rangeText = selectedRange ? ` a ${selectedRange}m` : "";

    // Verifica se il danno è valido (non NaN)
    const isDamageValid = !isNaN(evalDamage) && isFinite(evalDamage);

    // Costruisci il messaggio: se il danno è NaN, non mostrarlo
    let message = isDamageValid
      ? `<b>${characterToUse.characterInfo.name}</b> ha usato <b>${weaponToUse.name}</b>${rangeText}: Ha ottenuto <b>${dice}/${skillValue}</b> e infligge <b>${evalDamage}</b> danni ${damageFormula}`
      : `<b>${characterToUse.characterInfo.name}</b> ha usato <b>${weaponToUse.name}</b>${rangeText}: Ha ottenuto <b>${dice}/${skillValue}</b>`;

    let speachText = null;
    switch (level) {
      case "critic":
        speachText = isDamageValid
          ? `<b>${nickname}</b> ha fatto un tiro critico con <b>${weaponToUse.name}</b>${rangeText} facendo ${evalDamage} danni`
          : `<b>${nickname}</b> ha fatto un tiro critico con <b>${weaponToUse.name}</b>${rangeText}`;
        break;
      case "extreme":
        speachText = isDamageValid
          ? `<b>${nickname}</b> ha fatto un tiro estremo con <b>${weaponToUse.name}</b>${rangeText} facendo ${evalDamage} danni`
          : `<b>${nickname}</b> ha fatto un tiro estremo con <b>${weaponToUse.name}</b>${rangeText}`;
        break;
      case "arduous":
        speachText = isDamageValid
          ? `<b>${nickname}</b> ha fatto un tiro arduo con <b>${weaponToUse.name}</b>${rangeText} facendo ${evalDamage} danni`
          : `<b>${nickname}</b> ha fatto un tiro arduo con <b>${weaponToUse.name}</b>${rangeText}`;
        break;
      case "normal":
        speachText = isDamageValid
          ? `<b>${nickname}</b> ha usato <b>${weaponToUse.name}</b>${rangeText} facendo ${evalDamage} danni`
          : `<b>${nickname}</b> ha usato <b>${weaponToUse.name}</b>${rangeText} con successo`;
        break;
      default:
        break;
    }
    if (socket) {
      sendSocketMessage("DICE_ROLL", { message, level, speachText });
    }
  };

  // Handler per salvare il personaggio (condiviso tra Desktop e Mobile)
  const handleSaveCharacter = (newCharacter) => {
    updateCharacter(newCharacter).then((response) => {
      setCharacterData(response.character);
      setEditMode(false);
      reloadCharacters();
    });
  };

  // Handler per cambiare modalità edit (solo Desktop, ma centralizzato qui)
  const handleSwitchEditMode = () => {
    setEditMode(!editMode);
  };

  // Wrapper per handleDiceWeaponRoll che passa characterData aggiornato
  const handleDiceWeaponRollWithCurrentData = (
    weapon,
    selectedRange = null,
    selectedDamage = null,
  ) => {
    return handleDiceWeaponRoll(
      weapon,
      selectedRange,
      selectedDamage,
      characterData,
    );
  };

  if (isMobile) {
    return (
      <CharacterDetailTemplateMobile
        character={characterData}
        handleDiceRoll={handleDiceRoll}
        handleDiceWeaponRoll={handleDiceWeaponRollWithCurrentData}
        handleSaveCharacter={handleSaveCharacter}
        editMode={editMode}
        handleSwitchEditMode={handleSwitchEditMode}
      />
    );
  } else {
    return (
      <CharacterDetailTemplateDesktop
        character={characterData}
        handleDiceRoll={handleDiceRoll}
        handleDiceWeaponRoll={handleDiceWeaponRollWithCurrentData}
        handleSaveCharacter={handleSaveCharacter}
        editMode={editMode}
        handleSwitchEditMode={handleSwitchEditMode}
      />
    );
  }
}

export default CharacterDetailTemplate;
